import { PublicRequestError } from '../api/public.ts'

const defaultActivityRetryDelaysMs = [1_000, 3_000, 10_000] as const

export type PlayerActivityReconcileQueue = {
  request: () => void
  reset: () => void
  dispose: () => void
}

type PlayerActivityReconcileOptions = {
  active: () => boolean
  reconcile: () => Promise<void>
  onExhausted: (error: unknown) => void
  retryDelaysMs?: readonly number[]
  schedule?: (callback: () => void, delayMs: number) => unknown
  cancel?: (handle: unknown) => void
}

export function createPlayerActivityReconcileQueue(
  options: PlayerActivityReconcileOptions,
): PlayerActivityReconcileQueue {
  const retryDelays = options.retryDelaysMs?.length
    ? options.retryDelaysMs
    : defaultActivityRetryDelaysMs
  const schedule = options.schedule ?? (
    (callback, delayMs) => window.setTimeout(callback, delayMs)
  )
  const cancel = options.cancel ?? (
    handle => window.clearTimeout(handle as number)
  )

  let epoch = 0
  let timer: unknown
  let inFlightEpoch: number | null = null
  let pending = false
  let retryAttempt = 0
  let disposed = false

  function request(): void {
    if (disposed || !options.active()) return
    pending = true
    scheduleFlush(0)
  }

  function scheduleFlush(delayMs: number): void {
    if (
      disposed ||
      !pending ||
      inFlightEpoch === epoch ||
      timer !== undefined
    ) {
      return
    }
    const scheduledEpoch = epoch
    const handle = schedule(() => {
      if (disposed || epoch !== scheduledEpoch || timer !== handle) return
      timer = undefined
      void flush(scheduledEpoch)
    }, delayMs)
    timer = handle
  }

  async function flush(runEpoch: number): Promise<void> {
    if (
      disposed ||
      epoch !== runEpoch ||
      !pending ||
      !options.active()
    ) {
      if (epoch === runEpoch) clearPending()
      return
    }
    pending = false
    inFlightEpoch = runEpoch
    try {
      await options.reconcile()
      if (disposed || epoch !== runEpoch) return
      retryAttempt = 0
    } catch (error) {
      if (disposed || epoch !== runEpoch) return
      if (!options.active()) {
        clearPending()
        return
      }
      if (retryAttempt < retryDelays.length) {
        pending = true
        const delay = retryDelayMs(error, retryDelays[retryAttempt] ?? 0)
        retryAttempt += 1
        inFlightEpoch = null
        scheduleFlush(delay)
        return
      }
      clearPending()
      inFlightEpoch = null
      try {
        options.onExhausted(error)
      } catch {
        // Reconnect is a final convergence fallback and must not poison the queue.
      }
      return
    } finally {
      if (epoch === runEpoch && inFlightEpoch === runEpoch) {
        inFlightEpoch = null
      }
    }
    if (pending) scheduleFlush(0)
  }

  function retryDelayMs(error: unknown, backoffMs: number): number {
    const retryAfterMs = error instanceof PublicRequestError
      ? Math.max(0, error.retryAfterSeconds ?? 0) * 1_000
      : 0
    return Math.max(0, backoffMs, retryAfterMs)
  }

  function clearPending(): void {
    pending = false
    retryAttempt = 0
  }

  function reset(): void {
    epoch += 1
    clearPending()
    inFlightEpoch = null
    if (timer !== undefined) {
      cancel(timer)
      timer = undefined
    }
  }

  function dispose(): void {
    if (disposed) return
    reset()
    disposed = true
  }

  return { request, reset, dispose }
}
